import React, { useState, useEffect, useCallback } from 'react';
import './TaskTracking.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faChevronDown,
    faChevronRight,
    faFilter,
    faSearch,
    faTasks,
    faProjectDiagram,
    faCheckCircle,
    faClock,
    faPause,
    faExclamationCircle,
    faComment,
    faLink,
    faImage,
    faPaperPlane,
    faSpinner,
    faTimes,
    faCalendarAlt,
    faSquare
} from '@fortawesome/free-solid-svg-icons';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { supabase } from '../supabaseClient';

const TaskTracking = () => {
    const [user] = useAuthState(auth);
    const userEmail = user?.email || 'anonymous@example.com';



    const TASK_TYPES = ['Feature', 'Bug', 'Experiment', 'TechDebt', 'Other'];
    const PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
    const STATUSES = ['Todo', 'InProgress', 'Hold', 'Completed'];

    // State management
    const [tasks, setTasks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('kanban');
    const [selectedProject, setSelectedProject] = useState('all');
    const [draggedTask, setDraggedTask] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [selectedType, setSelectedType] = useState('all');
    const [selectedPriority, setSelectedPriority] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [expandedTasks, setExpandedTasks] = useState(new Set());
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [savingComment, setSavingComment] = useState(false);
    const [saving, setSaving] = useState(false);

    // Modal subtask management
    const [modalSubtasks, setModalSubtasks] = useState([]);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

    // Project form
    const [projectForm, setProjectForm] = useState({
        name: '',
        color: '#333333',
        description: ''
    });

    // Task form state
    const [taskForm, setTaskForm] = useState({
        title: '',
        project_id: '',
        task_type: 'Feature',
        type_other: '',
        priority: 'P1',
        status: 'Todo',
        details: '',
        deadline: '',
        technical_resources: []
    });

    // Resource form state
    const [resourceLink, setResourceLink] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);

    // =================== FETCH DATA ===================
    const fetchProjects = useCallback(async () => {
        const { data, error } = await supabase
            .from('tasks_projects')
            .select('*')
            .order('created_at', { ascending: false });
        if (!error && data) setProjects(data);
    }, []);

    const fetchTasks = useCallback(async () => {
        const { data, error } = await supabase
            .from('task_management_table')
            .select('*, subtasks(*)')
            .order('created_at', { ascending: false });
        if (!error && data) setTasks(data);
    }, []);

    const fetchComments = useCallback(async (taskId) => {
        const { data, error } = await supabase
            .from('task_comments')
            .select('*')
            .eq('task_id', taskId)
            .order('created_at', { ascending: true });
        if (!error && data) setComments(data);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchProjects(), fetchTasks()]);
            setLoading(false);
        };
        loadData();
    }, [fetchProjects, fetchTasks]);

    // =================== PROJECT CRUD ===================
    const handleAddProject = async () => {
        if (!projectForm.name.trim()) return;
        setSaving(true);
        const { data, error } = await supabase
            .from('tasks_projects')
            .insert([{
                name: projectForm.name,
                color: projectForm.color,
                description: projectForm.description,
                created_by_email: userEmail
            }])
            .select();
        if (!error && data) {
            setProjects([data[0], ...projects]);
            setShowProjectModal(false);
            setProjectForm({ name: '', color: '#333333', description: '' });
        }
        setSaving(false);
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm('Delete this project and ALL its tasks?')) return;
        const { error } = await supabase
            .from('tasks_projects')
            .delete()
            .eq('id', projectId);
        if (!error) {
            setProjects(projects.filter(p => p.id !== projectId));
            setTasks(tasks.filter(t => t.project_id !== projectId));
        }
    };

    // =================== TASK CRUD ===================
    const handleAddTask = async () => {
        if (!taskForm.title.trim() || !taskForm.project_id) return;
        setSaving(true);
        const { data, error } = await supabase
            .from('task_management_table')
            .insert([{
                title: taskForm.title,
                project_id: taskForm.project_id,
                task_type: taskForm.task_type,
                type_other: taskForm.task_type === 'Other' ? taskForm.type_other : null,
                priority: taskForm.priority,
                status: taskForm.status,
                details: taskForm.details,
                deadline: taskForm.deadline || null,
                technical_resources: taskForm.technical_resources,
                created_by_email: userEmail
            }])
            .select('*, subtasks(*)');

        if (!error && data) {
            const newTask = data[0];
            // Add subtasks created in modal
            if (modalSubtasks.length > 0) {
                const subtasksToInsert = modalSubtasks.map(s => ({
                    task_id: newTask.id,
                    title: s.title,
                    status: s.status
                }));
                const { data: subData } = await supabase
                    .from('subtasks')
                    .insert(subtasksToInsert)
                    .select();
                if (subData) newTask.subtasks = subData;
            }
            setTasks([newTask, ...tasks]);
            setShowAddTaskModal(false);
            resetTaskForm();
        }
        setSaving(false);
    };

    const handleUpdateTask = async () => {
        if (!editingTask) return;
        setSaving(true);
        const { data, error } = await supabase
            .from('task_management_table')
            .update({
                title: taskForm.title,
                project_id: taskForm.project_id,
                task_type: taskForm.task_type,
                type_other: taskForm.task_type === 'Other' ? taskForm.type_other : null,
                priority: taskForm.priority,
                status: taskForm.status,
                details: taskForm.details,
                deadline: taskForm.deadline || null,
                technical_resources: taskForm.technical_resources
            })
            .eq('id', editingTask.id)
            .select('*, subtasks(*)');

        if (!error && data) {
            // Sync subtasks: delete removed, add new, update changed
            const existingIds = (editingTask.subtasks || []).map(s => s.id);
            const modalIds = modalSubtasks.filter(s => s.id && !s._isNew).map(s => s.id);

            // Delete removed subtasks
            const toDelete = existingIds.filter(id => !modalIds.includes(id));
            if (toDelete.length > 0) {
                await supabase.from('subtasks').delete().in('id', toDelete);
            }

            // Add new subtasks
            const newSubs = modalSubtasks.filter(s => s._isNew);
            if (newSubs.length > 0) {
                await supabase.from('subtasks').insert(
                    newSubs.map(s => ({ task_id: editingTask.id, title: s.title, status: s.status }))
                );
            }

            // Update existing subtasks
            const toUpdate = modalSubtasks.filter(s => s.id && !s._isNew);
            for (const sub of toUpdate) {
                await supabase.from('subtasks').update({ title: sub.title, status: sub.status }).eq('id', sub.id);
            }

            // Re-fetch task with updated subtasks
            const { data: refreshed } = await supabase
                .from('task_management_table')
                .select('*, subtasks(*)')
                .eq('id', editingTask.id)
                .single();

            if (refreshed) {
                setTasks(tasks.map(t => t.id === editingTask.id ? refreshed : t));
            }

            setEditingTask(null);
            setShowAddTaskModal(false);
            resetTaskForm();
        }
        setSaving(false);
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        const { error } = await supabase
            .from('task_management_table')
            .delete()
            .eq('id', taskId);
        if (!error) {
            setTasks(tasks.filter(t => t.id !== taskId));
        }
    };

    // =================== SUBTASK CRUD (Card view) ===================
    const handleAddSubtask = async (taskId, subtaskTitle) => {
        const { data, error } = await supabase
            .from('subtasks')
            .insert([{ task_id: taskId, title: subtaskTitle, status: 'Todo' }])
            .select();
        if (!error && data) {
            setTasks(tasks.map(task => {
                if (task.id === taskId) {
                    return { ...task, subtasks: [...(task.subtasks || []), data[0]] };
                }
                return task;
            }));
        }
    };

    const handleUpdateSubtaskStatus = async (taskId, subtaskId, newStatus) => {
        const { error } = await supabase
            .from('subtasks')
            .update({ status: newStatus })
            .eq('id', subtaskId);
        if (!error) {
            setTasks(tasks.map(task => {
                if (task.id === taskId) {
                    return {
                        ...task,
                        subtasks: (task.subtasks || []).map(sub =>
                            sub.id === subtaskId ? { ...sub, status: newStatus } : sub
                        )
                    };
                }
                return task;
            }));
        }
    };

    const handleDeleteSubtask = async (taskId, subtaskId) => {
        const { error } = await supabase
            .from('subtasks')
            .delete()
            .eq('id', subtaskId);
        if (!error) {
            setTasks(tasks.map(task => {
                if (task.id === taskId) {
                    return {
                        ...task,
                        subtasks: (task.subtasks || []).filter(sub => sub.id !== subtaskId)
                    };
                }
                return task;
            }));
        }
    };

    // =================== MODAL SUBTASK MANAGEMENT ===================
    const handleModalAddSubtask = () => {
        if (!newSubtaskTitle.trim()) return;
        setModalSubtasks([...modalSubtasks, {
            id: `temp-${Date.now()}`,
            title: newSubtaskTitle,
            status: 'Todo',
            _isNew: true
        }]);
        setNewSubtaskTitle('');
    };

    const handleModalToggleSubtask = (index) => {
        setModalSubtasks(modalSubtasks.map((s, i) => {
            if (i === index) {
                return { ...s, status: s.status === 'Completed' ? 'Todo' : 'Completed' };
            }
            return s;
        }));
    };

    const handleModalDeleteSubtask = (index) => {
        setModalSubtasks(modalSubtasks.filter((_, i) => i !== index));
    };

    // =================== COMMENTS ===================
    const handleAddComment = async (taskId) => {
        if (!newComment.trim()) return;
        setSavingComment(true);
        const { data, error } = await supabase
            .from('task_comments')
            .insert([{
                task_id: taskId,
                comment: newComment,
                user_email: userEmail
            }])
            .select();
        if (!error && data) {
            setComments([...comments, data[0]]);
            setNewComment('');
        }
        setSavingComment(false);
    };

    const openComments = async (taskId) => {
        setShowCommentModal(taskId);
        await fetchComments(taskId);
    };

    // =================== IMAGE UPLOAD ===================
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingImage(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `task-resources/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('task-management-assets')
            .upload(filePath, file);

        if (!uploadError) {
            // Use signed URL (valid for 1 year) - works even if bucket is not public
            const { data: signedData, error: signedError } = await supabase.storage
                .from('task-management-assets')
                .createSignedUrl(filePath, 60 * 60 * 24 * 365);

            const imageUrl = signedError
                ? supabase.storage.from('task-management-assets').getPublicUrl(filePath).data.publicUrl
                : signedData.signedUrl;

            const newResource = { type: 'image', url: imageUrl, name: file.name };
            setTaskForm({
                ...taskForm,
                technical_resources: [...taskForm.technical_resources, newResource]
            });
        }
        setUploadingImage(false);
    };

    const handleAddLink = () => {
        if (!resourceLink.trim()) return;
        const newResource = { type: 'link', url: resourceLink, name: resourceLink };
        setTaskForm({
            ...taskForm,
            technical_resources: [...taskForm.technical_resources, newResource]
        });
        setResourceLink('');
    };

    const handleRemoveResource = (index) => {
        setTaskForm({
            ...taskForm,
            technical_resources: taskForm.technical_resources.filter((_, i) => i !== index)
        });
    };

    // =================== HELPERS ===================
    const toggleTaskExpansion = (taskId) => {
        const newExpanded = new Set(expandedTasks);
        if (newExpanded.has(taskId)) newExpanded.delete(taskId);
        else newExpanded.add(taskId);
        setExpandedTasks(newExpanded);
    };

    const resetTaskForm = () => {
        setTaskForm({
            title: '',
            project_id: projects[0]?.id || '',
            task_type: 'Feature',
            type_other: '',
            priority: 'P1',
            status: 'Todo',
            details: '',
            deadline: '',
            technical_resources: []
        });
        setResourceLink('');
        setModalSubtasks([]);
        setNewSubtaskTitle('');
    };

    const handleEditTask = async (task) => {
        setEditingTask(task);
        setTaskForm({
            title: task.title,
            project_id: task.project_id,
            task_type: task.task_type,
            type_other: task.type_other || '',
            priority: task.priority,
            status: task.status,
            details: task.details || '',
            deadline: task.deadline || '',
            technical_resources: task.technical_resources || []
        });
        // Load existing subtasks into modal
        setModalSubtasks((task.subtasks || []).map(s => ({ ...s, _isNew: false })));
        setNewSubtaskTitle('');
        setShowAddTaskModal(true);
        // Auto-fetch comments for this task
        setComments([]);
        setNewComment('');
        await fetchComments(task.id);
    };

    const getProjectById = (projectId) => projects.find(p => p.id === projectId);


    const getStatusIcon = (status) => {
        switch (status) {
            case 'Todo': return faClock;
            case 'InProgress': return faExclamationCircle;
            case 'Hold': return faPause;
            case 'Completed': return faCheckCircle;
            default: return faClock;
        }
    };

    const isOverdue = (deadline) => {
        if (!deadline) return false;
        return new Date(deadline) < new Date() && new Date(deadline).toDateString() !== new Date().toDateString();
    };

    const formatDeadline = (deadline) => {
        if (!deadline) return null;
        const d = new Date(deadline);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // =================== DRAG & DROP ===================
    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
        // Make the drag image slightly transparent
        setTimeout(() => {
            e.target.style.opacity = '0.4';
        }, 0);
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedTask(null);
        setDragOverColumn(null);
    };

    const handleDragOver = (e, status) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumn(status);
    };

    const handleDragLeave = () => {
        setDragOverColumn(null);
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        setDragOverColumn(null);
        if (!draggedTask || draggedTask.status === newStatus) return;

        // Optimistic UI update
        setTasks(tasks.map(t =>
            t.id === draggedTask.id ? { ...t, status: newStatus } : t
        ));

        // Persist to Supabase
        const { error } = await supabase
            .from('task_management_table')
            .update({ status: newStatus })
            .eq('id', draggedTask.id);

        if (error) {
            // Revert on failure
            setTasks(tasks.map(t =>
                t.id === draggedTask.id ? { ...t, status: draggedTask.status } : t
            ));
        }
        setDraggedTask(null);
    };

    const filteredTasks = tasks.filter(task => {
        if (selectedProject !== 'all' && task.project_id !== selectedProject) return false;
        if (selectedType !== 'all' && task.task_type !== selectedType) return false;
        if (selectedPriority !== 'all' && task.priority !== selectedPriority) return false;
        if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });

    // =================== RENDER TASK CARD ===================
    const renderTaskCard = (task) => {
        const project = getProjectById(task.project_id);
        const isExpanded = expandedTasks.has(task.id);
        const subtasks = task.subtasks || [];
        const completedSubtasks = subtasks.filter(sub => sub.status === 'Completed').length;
        const totalSubtasks = subtasks.length;
        const displayType = task.task_type === 'Other' && task.type_other
            ? task.type_other : task.task_type;
        const overdue = isOverdue(task.deadline);

        return (
            <div
                key={task.id}
                className={`task-card ${overdue ? 'task-overdue' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, task)}
                onDragEnd={handleDragEnd}
            >
                <div className="task-card-header">
                    <div className="task-title-row">
                        {subtasks.length > 0 && (
                            <button className="expand-btn" onClick={() => toggleTaskExpansion(task.id)}>
                                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                            </button>
                        )}
                        <h4 className="task-title">{task.title}</h4>
                    </div>
                    <div className="task-actions">
                        <button className="icon-btn" onClick={() => openComments(task.id)} title="Comments">
                            <FontAwesomeIcon icon={faComment} />
                        </button>
                        <button className="icon-btn" onClick={() => handleEditTask(task)}>
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="icon-btn delete" onClick={() => handleDeleteTask(task.id)}>
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>

                <div className="task-meta">
                    <span className="task-badge project-badge" style={{ backgroundColor: project?.color || '#333' }}>
                        {project?.name || 'Unknown'}
                    </span>

                    <span className={`task-badge priority-badge priority-${task.priority.toLowerCase()}`}>
                        {task.priority}
                    </span>
                    <span className={`task-badge type-badge type-${task.task_type.toLowerCase()}`}>
                        {displayType}
                    </span>
                </div>

                {task.details && <p className="task-details">{task.details}</p>}

                {/* Deadline */}
                {task.deadline && (
                    <div className={`task-deadline ${overdue ? 'deadline-overdue' : ''}`}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>{formatDeadline(task.deadline)}</span>
                        {overdue && <span className="overdue-label">OVERDUE</span>}
                    </div>
                )}

                {/* Technical Resources */}
                {task.technical_resources && task.technical_resources.length > 0 && (
                    <div className="task-resources">
                        {task.technical_resources.map((res, idx) => (
                            <a key={idx} href={res.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                                <FontAwesomeIcon icon={res.type === 'image' ? faImage : faLink} />
                                <span>{res.name?.substring(0, 30) || 'Resource'}</span>
                            </a>
                        ))}
                    </div>
                )}

                {/* Subtask Checklist Summary */}
                {totalSubtasks > 0 && (
                    <div className="subtask-checklist-summary">
                        <div className="checklist-progress">
                            <div className="checklist-bar">
                                <div className="checklist-fill" style={{ width: `${totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0}%` }}></div>
                            </div>
                            <span className="checklist-count">{completedSubtasks}/{totalSubtasks}</span>
                        </div>
                    </div>
                )}

                {/* Expanded Subtask Checklist */}
                {isExpanded && subtasks.length > 0 && (
                    <div className="subtasks-container">
                        {subtasks.map(subtask => (
                            <div key={subtask.id} className={`checklist-item ${subtask.status === 'Completed' ? 'checked' : ''}`}>
                                <button
                                    className="checkbox-btn"
                                    onClick={() => handleUpdateSubtaskStatus(
                                        task.id, subtask.id,
                                        subtask.status === 'Completed' ? 'Todo' : 'Completed'
                                    )}
                                >
                                    <FontAwesomeIcon icon={subtask.status === 'Completed' ? faCheckCircle : faSquare} />
                                </button>
                                <span className={`checklist-title ${subtask.status === 'Completed' ? 'completed' : ''}`}>
                                    {subtask.title}
                                </span>
                                <button className="icon-btn delete small" onClick={() => handleDeleteSubtask(task.id, subtask.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        ))}
                        <button
                            className="add-subtask-btn"
                            onClick={() => {
                                const title = prompt('Enter subtask title:');
                                if (title) handleAddSubtask(task.id, title);
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add Subtask
                        </button>
                    </div>
                )}

                <div className="task-footer">
                    <span className="created-by-tag">by {task.created_by_email}</span>
                </div>
            </div>
        );
    };

    // =================== VIEWS ===================
    const renderKanbanView = () => (
        <div className="kanban-board">
            {STATUSES.map(status => (
                <div
                    key={status}
                    className={`kanban-column ${dragOverColumn === status ? 'kanban-column-dragover' : ''}`}
                    onDragOver={(e) => handleDragOver(e, status)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, status)}
                >
                    <div className="kanban-column-header">
                        <FontAwesomeIcon icon={getStatusIcon(status)} />
                        <h3>{status}</h3>
                        <span className="task-count">
                            {filteredTasks.filter(t => t.status === status).length}
                        </span>
                    </div>
                    <div className="kanban-column-content">
                        {filteredTasks.filter(task => task.status === status).map(task => renderTaskCard(task))}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderListView = () => (
        <div className="list-view">
            <table className="tasks-table">
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Project</th>
                        <th>Type</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Deadline</th>
                        <th>Created By</th>
                        <th>Subtasks</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredTasks.map(task => {
                        const project = getProjectById(task.project_id);
                        const subtasks = task.subtasks || [];
                        const completedSubtasks = subtasks.filter(sub => sub.status === 'Completed').length;
                        const displayType = task.task_type === 'Other' && task.type_other
                            ? task.type_other : task.task_type;
                        const overdue = isOverdue(task.deadline);

                        return (
                            <tr key={task.id} className={overdue ? 'row-overdue' : ''}>
                                <td className="task-title-cell">{task.title}</td>
                                <td>
                                    <span className="task-badge" style={{ backgroundColor: project?.color || '#333' }}>
                                        {project?.name || 'Unknown'}
                                    </span>
                                </td>

                                <td>
                                    <span className={`task-badge type-badge type-${task.task_type.toLowerCase()}`}>
                                        {displayType}
                                    </span>
                                </td>
                                <td>
                                    <span className={`task-badge priority-badge priority-${task.priority.toLowerCase()}`}>
                                        {task.priority}
                                    </span>
                                </td>
                                <td>
                                    <span className={`task-badge status-badge status-${task.status.toLowerCase()}`}>
                                        {task.status}
                                    </span>
                                </td>
                                <td className={overdue ? 'deadline-overdue-text' : ''}>
                                    {formatDeadline(task.deadline) || '—'}
                                </td>
                                <td className="created-by-cell">{task.created_by_email}</td>
                                <td>{completedSubtasks}/{subtasks.length}</td>
                                <td className="actions-cell">

                                    <button className="icon-btn" onClick={() => handleEditTask(task)}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button className="icon-btn delete" onClick={() => handleDeleteTask(task.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const renderProjectView = () => (
        <div className="project-view">
            {projects.map(project => {
                const projectTasks = filteredTasks.filter(t => t.project_id === project.id);
                if (projectTasks.length === 0) return null;
                return (
                    <div key={project.id} className="project-section">
                        <div className="project-header" style={{ borderLeft: `6px solid ${project.color}` }}>
                            <FontAwesomeIcon icon={faProjectDiagram} />
                            <h2>{project.name}</h2>
                            <span className="task-count">{projectTasks.length} tasks</span>
                        </div>
                        <div className="project-tasks">
                            {projectTasks.map(task => renderTaskCard(task))}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // =================== LOADING STATE ===================
    if (loading) {
        return (
            <div className="task-tracking-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ textAlign: 'center', color: '#333' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                    <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Loading tasks...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="task-tracking-container">
            <div className="task-tracking-header">
                <h1>
                    <FontAwesomeIcon icon={faTasks} /> Task Tracking
                </h1>
                <div className="header-actions">
                    <button className="add-project-btn" onClick={() => setShowProjectModal(true)}>
                        <FontAwesomeIcon icon={faPlus} /> New Project
                    </button>
                    <button className="add-task-btn" onClick={() => { resetTaskForm(); setShowAddTaskModal(true); }}>
                        <FontAwesomeIcon icon={faPlus} /> Add Task
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Search tasks..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="filter-group">
                    <label><FontAwesomeIcon icon={faFilter} /> View:</label>
                    <select value={view} onChange={(e) => setView(e.target.value)}>
                        <option value="kanban">Kanban Board</option>
                        <option value="list">List View</option>
                        <option value="project">Project View</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>Project:</label>
                    <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
                        <option value="all">All Projects</option>
                        {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Type:</label>
                    <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                        <option value="all">All Types</option>
                        {TASK_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Priority:</label>
                    <select value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)}>
                        <option value="all">All Priorities</option>
                        {PRIORITIES.map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Statistics */}
            <div className="stats-section">
                <div className="stat-card">
                    <h3>{tasks.length}</h3>
                    <p>Total Tasks</p>
                </div>
                <div className="stat-card">
                    <h3>{tasks.filter(t => t.status === 'InProgress').length}</h3>
                    <p>In Progress</p>
                </div>
                <div className="stat-card">
                    <h3>{tasks.filter(t => t.status === 'Completed').length}</h3>
                    <p>Completed</p>
                </div>
                <div className="stat-card">
                    <h3>{tasks.reduce((acc, task) => acc + (task.subtasks?.length || 0), 0)}</h3>
                    <p>Total Subtasks</p>
                </div>
            </div>

            {/* Main content */}
            <div className="task-content">
                {view === 'kanban' && renderKanbanView()}
                {view === 'list' && renderListView()}
                {view === 'project' && renderProjectView()}
            </div>

            {/* ======== ADD/EDIT TASK MODAL (SPLIT LAYOUT) ======== */}
            {showAddTaskModal && (
                <div className="modal-overlay" onClick={() => { setShowAddTaskModal(false); setEditingTask(null); resetTaskForm(); setComments([]); setNewComment(''); }}>
                    <div className={`modal-content ${editingTask ? 'modal-split' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-row">
                            <h2>{editingTask ? 'Edit Task' : 'Add New Task'}</h2>
                            <span className="modal-email-tag">as: {userEmail}</span>
                        </div>

                        <div className={editingTask ? 'modal-split-body' : ''}>
                            {/* ===== LEFT PANEL: TASK FORM ===== */}
                            <div className={editingTask ? 'modal-panel-left' : ''}>

                                <div className="form-group">
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        value={taskForm.title}
                                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                                        placeholder="Enter task title"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Project *</label>
                                    <select
                                        value={taskForm.project_id}
                                        onChange={(e) => setTaskForm({ ...taskForm, project_id: e.target.value })}
                                    >
                                        <option value="">Select Project</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Type *</label>
                                        <select
                                            value={taskForm.task_type}
                                            onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                                        >
                                            {TASK_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        {taskForm.task_type === 'Other' && (
                                            <input
                                                type="text"
                                                value={taskForm.type_other}
                                                onChange={(e) => setTaskForm({ ...taskForm, type_other: e.target.value })}
                                                placeholder="Specify type..."
                                                style={{ marginTop: '0.5rem' }}
                                            />
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Priority *</label>
                                        <select
                                            value={taskForm.priority}
                                            onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                                        >
                                            {PRIORITIES.map(priority => (
                                                <option key={priority} value={priority}>{priority}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Status *</label>
                                        <select
                                            value={taskForm.status}
                                            onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                                        >
                                            {STATUSES.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label><FontAwesomeIcon icon={faCalendarAlt} /> Deadline</label>
                                        <input
                                            type="date"
                                            value={taskForm.deadline}
                                            onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Details</label>
                                    <textarea
                                        value={taskForm.details}
                                        onChange={(e) => setTaskForm({ ...taskForm, details: e.target.value })}
                                        placeholder="Enter task details"
                                        rows="3"
                                    />
                                </div>

                                {/* Subtasks Checklist in Modal */}
                                <div className="form-group">
                                    <label><FontAwesomeIcon icon={faTasks} /> Subtasks / Checklist</label>
                                    <div className="modal-subtasks-list">
                                        {modalSubtasks.map((sub, idx) => (
                                            <div key={sub.id || idx} className={`modal-checklist-item ${sub.status === 'Completed' ? 'checked' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="checkbox-btn"
                                                    onClick={() => handleModalToggleSubtask(idx)}
                                                >
                                                    <FontAwesomeIcon icon={sub.status === 'Completed' ? faCheckCircle : faSquare} />
                                                </button>
                                                <span className={sub.status === 'Completed' ? 'line-through' : ''}>
                                                    {sub.title}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="icon-btn delete small"
                                                    onClick={() => handleModalDeleteSubtask(idx)}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="add-subtask-row">
                                        <input
                                            type="text"
                                            value={newSubtaskTitle}
                                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                            placeholder="Add a subtask..."
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleModalAddSubtask(); } }}
                                        />
                                        <button type="button" className="btn-icon" onClick={handleModalAddSubtask}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </div>
                                </div>

                                {/* Technical Resources */}
                                <div className="form-group">
                                    <label>Technical Resources (Links & Images)</label>
                                    <div className="resources-input-row">
                                        <input
                                            type="text"
                                            value={resourceLink}
                                            onChange={(e) => setResourceLink(e.target.value)}
                                            placeholder="Paste a link..."
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLink(); } }}
                                        />
                                        <button type="button" className="btn-icon" onClick={handleAddLink}>
                                            <FontAwesomeIcon icon={faLink} />
                                        </button>
                                        <label className="btn-icon upload-label">
                                            <FontAwesomeIcon icon={uploadingImage ? faSpinner : faImage} spin={uploadingImage} />
                                            <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                                        </label>
                                    </div>
                                    {taskForm.technical_resources.length > 0 && (
                                        <div className="resources-list">
                                            {taskForm.technical_resources.map((res, idx) => (
                                                <div key={idx} className={`resource-item ${res.type === 'image' ? 'resource-item-image' : ''}`}>
                                                    {res.type === 'image' ? (
                                                        <a href={res.url} target="_blank" rel="noopener noreferrer" className="resource-thumbnail-link">
                                                            <img
                                                                src={res.url}
                                                                alt={res.name || 'Uploaded image'}
                                                                className="resource-thumbnail"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.parentNode.innerHTML = '<div class="resource-thumbnail-fallback">⚠️ Image unavailable</div>';
                                                                }}
                                                            />
                                                        </a>
                                                    ) : (
                                                        <a href={res.url} target="_blank" rel="noopener noreferrer" className="resource-link-url">
                                                            <FontAwesomeIcon icon={faLink} />
                                                            <span>{res.name?.substring(0, 50) || res.url?.substring(0, 50)}</span>
                                                        </a>
                                                    )}
                                                    <button className="icon-btn delete small resource-remove-btn" onClick={() => handleRemoveResource(idx)}>
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* ===== RIGHT PANEL: COMMENTS (only when editing) ===== */}
                            {editingTask && (
                                <div className="modal-panel-right">
                                    <h3 className="panel-title">
                                        <FontAwesomeIcon icon={faComment} /> Comments
                                    </h3>
                                    <div className="panel-comments-list">
                                        {comments.length === 0 && (
                                            <p className="no-comments">No comments yet.</p>
                                        )}
                                        {comments.map(c => (
                                            <div key={c.id} className="comment-item">
                                                <div className="comment-header">
                                                    <span className="comment-email">{c.user_email}</span>
                                                    <span className="comment-time">
                                                        {new Date(c.created_at).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="comment-text">{c.comment}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="comment-input-row">
                                        <input
                                            type="text"
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            placeholder="Write a comment..."
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(editingTask.id); }}
                                        />
                                        <button
                                            className="btn-primary btn-sm"
                                            onClick={() => handleAddComment(editingTask.id)}
                                            disabled={!newComment.trim() || savingComment}
                                        >
                                            <FontAwesomeIcon icon={savingComment ? faSpinner : faPaperPlane} spin={savingComment} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => { setShowAddTaskModal(false); setEditingTask(null); resetTaskForm(); setComments([]); setNewComment(''); }}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={editingTask ? handleUpdateTask : handleAddTask}
                                disabled={!taskForm.title || !taskForm.project_id || saving}
                            >
                                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : (editingTask ? 'Update Task' : 'Add Task')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ======== PROJECT MODAL ======== */}
            {showProjectModal && (
                <div className="modal-overlay" onClick={() => setShowProjectModal(false)}>
                    <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Project</h2>
                        <div className="form-group">
                            <label>Project Name *</label>
                            <input
                                type="text"
                                value={projectForm.name}
                                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                                placeholder="Enter project name"
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Color</label>
                                <input
                                    type="color"
                                    value={projectForm.color}
                                    onChange={(e) => setProjectForm({ ...projectForm, color: e.target.value })}
                                    style={{ height: '40px', cursor: 'pointer' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <input
                                    type="text"
                                    value={projectForm.description}
                                    onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                                    placeholder="Short description"
                                />
                            </div>
                        </div>

                        {projects.length > 0 && (
                            <div className="existing-projects">
                                <label>Existing Projects:</label>
                                <div className="project-chips">
                                    {projects.map(p => (
                                        <div key={p.id} className="project-chip" style={{ borderLeft: `4px solid ${p.color}` }}>
                                            <span>{p.name}</span>
                                            <button className="icon-btn delete small" onClick={() => handleDeleteProject(p.id)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setShowProjectModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={handleAddProject} disabled={!projectForm.name || saving}>
                                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : 'Create Project'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ======== COMMENTS MODAL ======== */}
            {showCommentModal && (
                <div className="modal-overlay" onClick={() => { setShowCommentModal(null); setComments([]); setNewComment(''); }}>
                    <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
                        <h2>Comments</h2>
                        <div className="comments-list">
                            {comments.length === 0 && (
                                <p className="no-comments">No comments yet. Be the first to comment!</p>
                            )}
                            {comments.map(c => (
                                <div key={c.id} className="comment-item">
                                    <div className="comment-header">
                                        <span className="comment-email">{c.user_email}</span>
                                        <span className="comment-time">
                                            {new Date(c.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="comment-text">{c.comment}</p>
                                </div>
                            ))}
                        </div>
                        <div className="comment-input-row">
                            <input
                                type="text"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Write a comment..."
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(showCommentModal); }}
                            />
                            <button
                                className="btn-primary btn-sm"
                                onClick={() => handleAddComment(showCommentModal)}
                                disabled={!newComment.trim() || savingComment}
                            >
                                <FontAwesomeIcon icon={savingComment ? faSpinner : faPaperPlane} spin={savingComment} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskTracking;
